import { ApproveRequestDTO, ChangesetDTO, ChangesetOpDTO, ChangesetRebaseDTO, ChangesetRevisionDTO, ChangesetStates, RebaseOutcomes, SpacePageDTO } from "../models/ChangeRequestDTO";
import { CurrentContentState, ReviewItemPane, ReviewView } from "./ReviewItemPane";
import { IChangeRequestService, errorMessage } from "../services/ChangeRequestService";
import { PageTitles, describeOp } from "../utils/describeOp";
import { ReviewItem, ReviewKind, buildReview, pagesToDiff } from "../utils/reviewModel";
import { ReviewTree, TreeStatus } from "./ReviewTree";
import { Resolution, Resolutions, keepMatching, toResolutions, unresolved } from "../utils/conflicts";
import { conflictsReason, listWords, recapResolutions, sideLabels } from "../utils/labels";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Button } from "../../../lib/components/buttons/Button";
import { DateTimeHelper } from "../../../lib/utils/DateTimeHelper";
import { Loader } from "../../../lib/components/loader/Loader";
import { LockMarks } from "../../locks/models/LockDTO";
import { RebaseConflictsSummary } from "./RebaseConflicts";
import { ReviewKindDot } from "./ReviewKindBadge";

type Action = "approve" | "reject" | "withdraw";

interface IChangeRequestDetailProps {
    changesetId: string;
    service: IChangeRequestService;
    spaceId?: string;
    /** The space's pages (manifest): titles, tree and etags. */
    pages: SpacePageDTO[];
    currentUserId?: string;
    /** Lock badges of the space's pages, so changes on locked pages show it (OrbitalPlan/18 D8). */
    locks?: LockMarks;
    /** The changed page shown (`?page=` in the URL); the first one when absent or unknown. */
    selectedItemKey?: string;
    onSelectItem: (key: string) => void;
    /** Called after an approve/reject/withdraw/update went through, so the list refreshes. */
    onChanged: () => void;
    onClose: () => void;
}

const stateLabels: Record<string, string> = {
    [ChangesetStates.Proposed]: "Proposed",
    [ChangesetStates.Approved]: "Approved",
    [ChangesetStates.Applied]: "Applied",
    [ChangesetStates.Rejected]: "Rejected",
    [ChangesetStates.Failed]: "Failed",
    [ChangesetStates.Withdrawn]: "Withdrawn",
};

export const stateLabel = (state: string) => stateLabels[state] ?? state;

const outcomeTexts: Record<string, string> = {
    [ChangesetStates.Approved]: "Change request approved and applied.",
    [ChangesetStates.Applied]: "Change request approved and applied.",
    [ChangesetStates.Rejected]: "Change request rejected.",
    [ChangesetStates.Withdrawn]: "Change request withdrawn.",
    [ChangesetStates.Failed]: "The change request could not be applied.",
};

const forbiddenTexts: Record<Action, string> = {
    approve: "You can't approve change requests in this space: it needs the Ark Owner role. Ask a space owner to review it.",
    reject: "You can't reject change requests in this space: it needs the Ark Owner role. Ask a space owner to review it.",
    withdraw: "Only the author can withdraw this change request.",
};

/** "you" for the signed-in user, else the id (the review resolves ids to names through the directory where it can). */
export function authorLabel(authorId: string | null | undefined, currentUserId: string | undefined) {
    if (!authorId) return "unknown";
    return currentUserId && authorId === currentUserId ? "you" : authorId;
}

/** Ids shortened for display (the full one is the hover text). */
export function shortPerson(label: string) {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(label) ? `${label.substring(0, 8)}…` : label;
}

function actionError(err: any, action: Action): string {
    return err?.response?.status === 403 ? forbiddenTexts[action] : errorMessage(err);
}

const pages = (n: number) => (n === 1 ? "1 page" : `${n} pages`);
const changes = (n: number) => (n === 1 ? "1 change" : `${n} changes`);
const conflictsText = (n: number) => (n === 1 ? "1 conflict" : `${n} conflicts`);
const resolutionsText = (n: number) => (n === 1 ? "1 resolution" : `${n} resolutions`);

/** "2 pages changed", "1 page no longer exists", or both: what makes the change request stale. */
function staleSummary(stale: ReviewItem[]): string {
    const missing = stale.filter((i) => i.missing).length;
    const changed = stale.length - missing;
    const parts = [
        changed ? `${pages(changed)} changed` : "",
        missing ? `${pages(missing)} ${missing === 1 ? "no longer exists" : "no longer exist"}` : "",
    ].filter(Boolean);
    return parts.join(" and ");
}

const isTyping = (target: EventTarget | null) =>
    target instanceof HTMLElement && (["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName) || target.isContentEditable);

/** The revision of each change request this browser last read (a per-viewer convenience: the "updated since you read it" strip). */
const seenKey = (id: string) => `ark.cr.seen.${id}`;
function readSeen(id: string): number | undefined {
    try {
        const value = localStorage.getItem(seenKey(id));
        return value ? Number(value) || undefined : undefined;
    } catch {
        return undefined;
    }
}
function writeSeen(id: string, revision: number) {
    try {
        localStorage.setItem(seenKey(id), String(revision));
    } catch {
        /* private window or blocked storage: the strip just does not show next time */
    }
}

/** The proposed content and title per item key at one revision (what "Since the update" compares against). */
function proposedByKey(ops: ChangesetOpDTO[]): Map<string, { content?: string | null; title?: string }> {
    const map = new Map<string, { content?: string | null; title?: string }>();
    for (const op of ops) {
        const key = op.op === "create" ? (op.tempId ? `new:${op.tempId}` : undefined) : op.id != null ? `page:${op.id}` : undefined;
        if (!key) continue;
        const entry = map.get(key) ?? {};
        if (op.op === "create") entry.content = op.content ?? "";
        else if (op.op === "update") {
            if (op.content != null) entry.content = op.content;
            else if (op.clearContent) entry.content = null;
        }
        if (op.title != null) entry.title = op.title;
        map.set(key, entry);
    }
    return map;
}

type Strip = { tone: "info" | "ok" | "warn" | "error" | "muted"; body: React.ReactNode; aside?: React.ReactNode; label?: string };

/**
 * One change request as a review: what it changes page by page (tree of the changed pages, diff against the current version, rendered
 * preview), what an approve would run into (pages edited since the proposal), and the review actions. The header is one line; one
 * status strip under it says where the change request stands and what to do next.
 */
export function ChangeRequestDetail(props: IChangeRequestDetailProps) {
    const { service, changesetId, spaceId } = props;
    const [detail, setDetail] = useState<ChangesetDTO | undefined>();
    const [loadError, setLoadError] = useState<string | undefined>();
    const [contents, setContents] = useState<Record<number, string>>({});
    const [currentEtags, setCurrentEtags] = useState<Record<number, string>>({});
    const [contentState, setContentState] = useState<CurrentContentState>("loading");
    const [pending, setPending] = useState<Action | undefined>();
    // the approve also brings the proposal up to date first
    const [pendingUpdate, setPendingUpdate] = useState(false);
    const [reason, setReason] = useState("");
    const [reasonMissing, setReasonMissing] = useState(false);
    const [isBusy, setIsBusy] = useState(false);
    const [actionErrorText, setActionErrorText] = useState<string | undefined>();
    // what just happened, shown for a moment
    const [toast, setToast] = useState<{ text: string; tone: "ok" | "error" } | undefined>();
    // the body view the reviewer picked, kept from page to page
    const [view, setView] = useState<ReviewView | undefined>();
    // what an update would do (the server's dry run), refreshed whenever the change request is (re)loaded; or what the last update did
    const [preview, setPreview] = useState<ChangesetRebaseDTO | undefined>();
    const [previewState, setPreviewState] = useState<"loading" | "loaded" | "error">("loading");
    const [previewTick, setPreviewTick] = useState(0);
    // when this browser's own update saved: a check right after that still says "stale" is read again once before it is believed
    const [updatedAtMs, setUpdatedAtMs] = useState(0);
    const rechecked = useRef(false);
    // true once an Update ran into the conflicts shown (not only the check)
    const [attempted, setAttempted] = useState(false);
    // the answers to the conflicts, by conflict identity; they go with the next Update
    const [resolutions, setResolutions] = useState<Resolutions>({});
    // how many conflicts a reviewer settled in this session: the approve says so, since they then approve their own choices
    const [selfResolved, setSelfResolved] = useState(0);
    // the revisions (an updated proposal), and the one this browser last read when the proposal moved on since
    const [revisions, setRevisions] = useState<ChangesetRevisionDTO[]>([]);
    const [updatedSince, setUpdatedSince] = useState<number | undefined>();
    const [previous, setPrevious] = useState<ChangesetRevisionDTO | undefined>();
    // display names of the ids on screen (author, reviewer, page editors), from the directory
    const [names, setNames] = useState<Record<string, string>>({});

    const isOpen = detail?.state === ChangesetStates.Proposed;
    const revision = detail?.revision ?? 1;
    const review = useMemo(() => (detail ? buildReview(detail, props.pages, currentEtags) : undefined), [detail, props.pages, currentEtags]);
    const titles: PageTitles = useMemo(() => Object.fromEntries(props.pages.map((p) => [p.id, p.title])), [props.pages]);
    const diffIds = useMemo(() => (review && isOpen ? pagesToDiff(review).join(",") : ""), [review, isOpen]);

    useEffect(() => {
        let alive = true;
        setDetail(undefined);
        setLoadError(undefined);
        setUpdatedSince(undefined);
        setPrevious(undefined);
        service
            .get(changesetId)
            .then((cs) => {
                if (!alive) return;
                setDetail(cs);
                const rev = cs.revision ?? 1;
                const seen = readSeen(changesetId);
                if (seen !== undefined && rev > seen && cs.state === ChangesetStates.Proposed) setUpdatedSince(seen);
                writeSeen(changesetId, rev);
            })
            .catch((err) => alive && setLoadError(errorMessage(err)));
        return () => {
            alive = false;
        };
    }, [service, changesetId]);

    // the "before" side of the diffs: only meaningful while the change request is open (once applied, the page is the "after")
    useEffect(() => {
        if (!detail) return;
        if (!isOpen) {
            setContentState("unavailable");
            return;
        }
        if (!spaceId || !diffIds) {
            setContentState("loaded");
            return;
        }
        let alive = true;
        setContentState("loading");
        service
            .getPageContents(spaceId, diffIds.split(",").map(Number))
            .then((res) => {
                if (!alive) return;
                const bodies: Record<number, string> = {};
                const etags: Record<number, string> = {};
                for (const p of res) {
                    bodies[p.id] = p.content ?? "";
                    if (p.etag) etags[p.id] = p.etag;
                }
                setContents(bodies);
                setCurrentEtags(etags);
                setContentState("loaded");
            })
            .catch(() => alive && setContentState("error"));
        return () => {
            alive = false;
        };
    }, [service, spaceId, detail, isOpen, diffIds]);

    // what an update would do, for the author (to update) and the reviewer (to know whether it can be approved once updated)
    useEffect(() => {
        if (!detail) return;
        if (!isOpen) {
            setPreview(undefined);
            setPreviewState("loaded");
            return;
        }
        let alive = true;
        setPreviewState("loading");
        service
            .previewRebase(detail.id)
            .then((res) => {
                if (!alive) return;
                setPreview(res);
                setAttempted(false);
                setResolutions((prev) => keepMatching(prev, res.conflicts));
                setPreviewState("loaded");
            })
            .catch(() => alive && setPreviewState("error"));
        return () => {
            alive = false;
        };
    }, [service, detail, isOpen, previewTick]);

    useEffect(() => {
        if (!preview || previewState !== "loaded" || rechecked.current || Date.now() - updatedAtMs > 10000) return;
        if (preview.outcome !== RebaseOutcomes.UpToDate || !preview.issues.some((i) => i.code === "stale")) return;
        rechecked.current = true;
        const timer = setTimeout(() => setPreviewTick((t) => t + 1), 1500);
        return () => clearTimeout(timer);
    }, [preview, previewState, updatedAtMs]);

    // an updated proposal: its revisions (who updated it, what the update settled), and the revision this browser last read
    useEffect(() => {
        if (!detail || revision <= 1) {
            setRevisions([]);
            return;
        }
        let alive = true;
        service.getRevisions(detail.id).then((list) => alive && setRevisions(list)).catch(() => { /* the strip just says "updated" */ });
        return () => {
            alive = false;
        };
    }, [service, detail, revision]);

    useEffect(() => {
        if (!detail || updatedSince === undefined) return;
        let alive = true;
        service.getRevision(detail.id, updatedSince).then((rev) => alive && setPrevious(rev)).catch(() => { /* no "since the update" diff */ });
        return () => {
            alive = false;
        };
    }, [service, detail, updatedSince]);

    // names for the ids on screen; the directory may not know some (agents, deleted accounts), which stay as short ids
    const ids = useMemo(() => {
        const all = [detail?.authorId, detail?.reviewedBy, ...(review?.items.map((i) => i.modifiedBy) ?? []), ...revisions.map((r) => r.actor)];
        return Array.from(new Set(all.filter((id): id is string => !!id && id !== props.currentUserId))).sort().join(",");
    }, [detail, review, revisions, props.currentUserId]);
    useEffect(() => {
        if (!ids) return;
        let alive = true;
        service
            .getUserNames(ids.split(","))
            .then((found) => alive && setNames((prev) => ({ ...prev, ...Object.fromEntries(found.map((u) => [u.id, u.displayName])) })))
            .catch(() => { /* ids stay ids */ });
        return () => {
            alive = false;
        };
    }, [service, ids]);

    const ordered = useMemo(() => review?.ordered ?? [], [review]);
    const selectedIndex = Math.max(0, ordered.findIndex((i) => i.key === props.selectedItemKey));
    const selected: ReviewItem | undefined = ordered[selectedIndex];
    const { onSelectItem } = props;

    const goTo = useCallback((index: number) => {
        const item = ordered[index];
        if (item) onSelectItem(item.key);
    }, [ordered, onSelectItem]);

    useEffect(() => {
        const onKey = (ev: KeyboardEvent) => {
            if (isTyping(ev.target) || ev.ctrlKey || ev.metaKey || ev.altKey) return;
            if (ev.key === "j") goTo(selectedIndex + 1);
            else if (ev.key === "k") goTo(selectedIndex - 1);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [goTo, selectedIndex]);

    useEffect(() => {
        if (!toast) return;
        const timer = setTimeout(() => setToast(undefined), 8000);
        return () => clearTimeout(timer);
    }, [toast]);

    const person = useCallback((id: string | null | undefined) => {
        if (!id) return "unknown";
        if (props.currentUserId && id === props.currentUserId) return "you";
        return names[id] ?? shortPerson(id);
    }, [names, props.currentUserId]);

    const cancel = useCallback(() => {
        setPending(undefined);
        setPendingUpdate(false);
        setReasonMissing(false);
    }, []);

    const confirm = useCallback(() => {
        if (!pending || !detail) return;
        if (pending === "reject" && !reason.trim()) {
            setReasonMissing(true);
            return;
        }
        const action = pending;
        // the revision read goes with the approve, so one that moved on since is refused; `update` brings it up to date first
        const approval: ApproveRequestDTO = {};
        if (detail.revision) approval.revision = detail.revision;
        if (pendingUpdate) approval.update = true;
        const call =
            action === "approve" ? service.approve(detail.id, approval)
            : action === "reject" ? service.reject(detail.id, reason.trim())
            : service.withdraw(detail.id);

        setIsBusy(true);
        setActionErrorText(undefined);
        call
            .then((result) => {
                // The action responses carry no ops: keep the ones already shown.
                setDetail((prev) => ({ ...(prev as ChangesetDTO), ...result, ops: result.ops ?? prev?.ops }));
                if (result.revision) writeSeen(detail.id, result.revision);
                setToast({ text: outcomeTexts[result.state] ?? `Change request ${result.state}.`, tone: result.state === ChangesetStates.Failed ? "error" : "ok" });
                setPending(undefined);
                setPendingUpdate(false);
                setIsBusy(false);
                props.onChanged();
            })
            .catch((err) => {
                setActionErrorText(actionError(err, action));
                setIsBusy(false);
                // the proposal moved on since it was read: load it again, so the strip shows what changed
                if (err?.response?.data?.error?.code === "changeset-changed") {
                    setPending(undefined);
                    setPendingUpdate(false);
                    service.get(detail.id).then((cs) => {
                        setUpdatedSince(detail.revision ?? 1);
                        setDetail(cs);
                        writeSeen(detail.id, cs.revision ?? 1);
                    }).catch(() => { /* the error text stands */ });
                }
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pending, pendingUpdate, detail, reason, service, props.onChanged]);

    const conflicts = useMemo(() => (preview?.outcome === RebaseOutcomes.Conflicts ? preview.conflicts : []), [preview]);
    const merges = useMemo(() => preview?.merges ?? [], [preview]);
    const left = useMemo(() => unresolved(conflicts, resolutions), [conflicts, resolutions]);

    // the author merges the edits made since into the change request (all or nothing: on a conflict, nothing changes), with the
    // choices made for the conflicts the check reported
    const update = useCallback(() => {
        if (!detail) return;
        setIsBusy(true);
        setActionErrorText(undefined);
        const sent = toResolutions(conflicts, resolutions);
        (sent.length ? service.rebase(detail.id, { resolutions: sent }) : service.rebase(detail.id))
            .then((res) => {
                setIsBusy(false);
                if (res.outcome === RebaseOutcomes.Conflicts) {
                    // the pages may have moved on since the check: the conflicts are the ones of this attempt
                    setPreview(res);
                    setAttempted(true);
                    setResolutions((prev) => keepMatching(prev, res.conflicts));
                    // open a page with conflicts, unless the one shown has some
                    const first = res.conflicts[0];
                    if (first && !res.conflicts.some((c) => `page:${c.pageId}` === props.selectedItemKey)) onSelectItem(`page:${first.pageId}`);
                    return;
                }
                // the changeset returned is the rebased one: loading it refreshes the check too
                if (res.changeset) {
                    setDetail(res.changeset);
                    writeSeen(detail.id, res.changeset.revision ?? 1);
                }
                rechecked.current = false;
                setUpdatedAtMs(Date.now());
                if (sent.length && detail.authorId !== props.currentUserId) setSelfResolved((n) => n + sent.length);
                setResolutions({});
                if (res.outcome === RebaseOutcomes.Rebased) {
                    setToast({ text: `Updated: the edits made since were merged into ${pages(res.pages.length)}${sent.length ? ` with your ${resolutionsText(sent.length)}` : ""}.`, tone: "ok" });
                    props.onChanged();
                } else {
                    setToast({ text: "Already up to date: no page changed since the proposal.", tone: "ok" });
                }
            })
            .catch((err) => {
                setActionErrorText(err?.response?.status === 403 ? "You can't update this change request." : errorMessage(err));
                setIsBusy(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [detail, service, props.onChanged, props.selectedItemKey, props.currentUserId, onSelectItem, conflicts, resolutions]);

    const resolve = useCallback((key: string, resolution: Resolution | undefined) => {
        setResolutions((prev) => {
            const next = { ...prev };
            if (resolution) next[key] = resolution;
            else delete next[key];
            return next;
        });
    }, []);

    // what the last update changed in the proposal since this browser last read it, by item key
    const previousByKey = useMemo(() => (previous?.ops ? proposedByKey(previous.ops) : undefined), [previous]);
    const changedSince = useMemo(() => {
        const keys = new Set<string>();
        if (!previousByKey || !review) return keys;
        for (const item of review.items) {
            const was = previousByKey.get(item.key);
            const content = item.proposedContent;
            if (!was) keys.add(item.key);
            else if ((was.content ?? "") !== (content ?? "") || (was.title !== undefined && was.title !== item.title)) keys.add(item.key);
        }
        return keys;
    }, [previousByKey, review]);

    const conflictsOn = useCallback((item: ReviewItem) => conflicts.filter((c) => c.pageId === item.pageId), [conflicts]);
    const mergeOf = useCallback((item: ReviewItem) => merges.find((m) => m.pageId === item.pageId && m.content != null) ?? merges.find((m) => m.pageId === item.pageId), [merges]);

    const lockOf = useCallback((item: ReviewItem) => {
        if (!props.locks) return undefined;
        if (item.pageId !== undefined) return props.locks[item.pageId];
        const create = detail?.ops?.find((o) => o.op === "create" && `new:${o.tempId}` === item.key);
        const parent = Number(create?.parentRef);
        return create?.parentRef && Number.isFinite(parent) ? props.locks[parent] : undefined;
    }, [props.locks, detail]);

    // the tree sticks right under the (sticky, variable-height) header
    const [headerHeight, setHeaderHeight] = useState(0);
    const headerRef = useRef<HTMLElement | null>(null);
    const hasHeader = !!detail && !!review;
    useEffect(() => {
        const el = headerRef.current;
        if (!el || typeof ResizeObserver === "undefined") return;
        const observer = new ResizeObserver(() => setHeaderHeight(el.offsetHeight));
        observer.observe(el);
        return () => observer.disconnect();
    }, [hasHeader]);

    if (loadError) {
        return (
            <section className="change-request-review">
                <button type="button" className="review-back" onClick={props.onClose}>← Change requests</button>
                <div className="change-request-error">{loadError}</div>
            </section>
        );
    }

    if (!detail || !review) {
        return (
            <section className="change-request-review">
                <Loader />
            </section>
        );
    }

    const ops = detail.ops ?? [];
    const isAuthor = !!props.currentUserId && detail.authorId === props.currentUserId;
    const authorName = detail.authorId ? names[detail.authorId] : undefined;
    const summary = review.summary;
    const chips: [string, number, ReviewKind][] = [
        ["new", summary.created, "created"],
        ["edited", summary.updated, "updated"],
        ["deleted", summary.deleted, "deleted"],
        ["moved", summary.moved, "moved"],
        [summary.files === 1 ? "file" : "files", summary.files, "files"],
    ];
    const changeCount = ops.length || detail.opCount;
    const stale = isOpen ? review.stale : [];
    const staleText = stale.length ? staleSummary(stale) : preview?.pages.length ? `${pages(preview.pages.length)} changed` : "Pages changed";
    const editors = listWords(Array.from(new Set(stale.map((i) => person(i.modifiedBy)).filter((n) => n !== "unknown"))));
    // behind its pages by the etags seen here, or by the server's check (which also sees pages this review cannot)
    const isStale = isOpen && (stale.length > 0 || (!!preview && previewState === "loaded" && preview.outcome !== RebaseOutcomes.UpToDate));
    const mergesCleanly = isStale && preview?.outcome === RebaseOutcomes.Rebased;
    const blocked = conflicts.some((c) => c.code === "deleted" || c.code === "no-base");
    // anyone with write on the pages updates; the server refuses the rest with a 403 the strip explains
    const canUpdate = isStale && previewState !== "loading" && (!preview || preview.outcome !== RebaseOutcomes.Conflicts || left.length === 0) && !blocked;
    const resultFor = (index: number) => detail.results?.find((r) => r.index === index);
    const titleOfPage = (id: number) => ordered.find((i) => i.pageId === id)?.title ?? titles[id] ?? `Page ${id}`;
    const latest = revisions.length ? revisions[revisions.length - 1] : undefined;
    const deletedItems = review.items.filter((i) => i.kind === "deleted");
    const subpages = deletedItems.reduce((n, i) => n + i.childCount, 0);
    const issuesText = preview?.issues.length ? ` ${isStale ? "Even once updated, an" : "An"} approve would be refused: ${preview.issues.map((i) => i.message).join(" ")}` : "";

    const statusOf = (item: ReviewItem): TreeStatus | undefined => {
        if (!isOpen) return undefined;
        const onPage = conflicts.filter((c) => c.pageId === item.pageId);
        if (onPage.length) {
            const open = left.filter((c) => c.pageId === item.pageId).length;
            return open ? { kind: "conflicts", count: open } : { kind: "resolved" };
        }
        if (isStale && item.pageId !== undefined && (preview?.pages.includes(item.pageId) || merges.some((m) => m.pageId === item.pageId && m.clean))) return { kind: "merges" };
        if (changedSince.has(item.key)) return { kind: "changed" };
        return undefined;
    };

    const labelsFor = (item: ReviewItem) => {
        const editor = item.modifiedBy ? person(item.modifiedBy) : undefined;
        return sideLabels({ isAuthor, authorName, editorName: editor && editor !== "unknown" && editor !== "you" ? editor : undefined });
    };

    const rejectWithConflicts = () => {
        setReason(conflictsReason(conflicts, titleOfPage));
        setReasonMissing(false);
        setPending("reject");
    };

    // one strip: where the change request stands and what to do next
    let strip: Strip | undefined;
    if (actionErrorText) {
        strip = { tone: "error", body: actionErrorText, aside: <button type="button" className="review-link" onClick={() => setActionErrorText(undefined)}>Dismiss</button> };
    } else if (!isOpen) {
        const when = detail.appliedAt ? ` ${DateTimeHelper.formatDateTimeAsTimeAgo(detail.appliedAt)}` : "";
        const by = detail.reviewedBy ? ` by ${person(detail.reviewedBy)}` : "";
        if (detail.state === ChangesetStates.Failed) strip = { tone: "error", body: <><strong>Could not be applied.</strong> {detail.error ?? "Ark stopped at a change that failed."}</> };
        else if (detail.state === ChangesetStates.Rejected) strip = { tone: "error", body: <><strong>Rejected{by}{when}.</strong> {detail.reviewReason}</> };
        else if (detail.state === ChangesetStates.Withdrawn) strip = { tone: "muted", body: <>Withdrawn by {person(detail.authorId)}.</> };
        else strip = { tone: "ok", body: <><strong>Approved{by}{when}</strong> and applied to the space.{detail.error ? ` ${detail.error}` : ""}</> };
    } else if (isStale && conflicts.length) {
        strip = undefined; // the conflicts summary is its own strip
    } else if (isStale) {
        const where = `${staleText} since the proposal${editors ? `, edited by ${editors}` : ""}`;
        strip = {
            tone: "warn",
            body: previewState === "loading"
                ? <><strong>Behind its pages.</strong> {where}. Checking whether the edits merge…</>
                : mergesCleanly
                    ? <><strong>Behind its pages, but merges cleanly.</strong> {where}; the proposed changes fit around those edits.{" "}
                        {isAuthor ? "Update brings the change request up to date, or update and approve in one step." : "You can update and approve in one step, or wait for the author."}{issuesText}</>
                    : <><strong>Behind its pages.</strong> {where}. It can't be approved as it is: an approve would be refused, with nothing applied.{" "}
                        {isAuthor ? "Update merges the edits made since into your changes." : "Ask the author to update the change request."}{issuesText}</>,
            aside: previewState === "loaded" ? <button type="button" className="review-link" onClick={() => setPreviewTick((t) => t + 1)}>Check again</button> : undefined,
        };
    } else if (preview?.issues.length) {
        strip = {
            tone: "warn",
            body: <><strong>An approve would be refused:</strong> {preview.issues.map((i) => i.message).join(" ")}</>,
            aside: <button type="button" className="review-link" onClick={() => setPreviewTick((t) => t + 1)}>Check again</button>,
        };
    } else if (updatedSince !== undefined && revision > updatedSince) {
        const who = person(latest?.actor);
        const recap = latest
            ? `${who.charAt(0).toUpperCase()}${who.slice(1)} merged the edits made since into ${pages(latest.pages.length)}${latest.resolutions.length ? ` and settled ${conflictsText(latest.resolutions.length)}: ${listWords(recapResolutions(latest.resolutions, titleOfPage))}` : ""}.`
            : "";
        strip = {
            tone: "info",
            label: "Updated since you read it",
            body: <><strong>Updated since you last read it</strong>{detail.updatedAt ? ` (${DateTimeHelper.formatDateTimeAsTimeAgo(detail.updatedAt)})` : ""}. {recap}{" "}
                {changedSince.size ? `“Since the update” on ${changedSince.size === 1 ? "the changed page" : `the ${changedSince.size} changed pages`} shows what moved. ` : ""}
                {isAuthor ? "You can approve it yourself or wait for a review." : "Ready to approve."}</>,
        };
    } else if (previewState === "loaded" || previewState === "error") {
        strip = isAuthor
            ? { tone: "ok", body: <><strong>Up to date with its pages.</strong> You can approve it yourself or wait for a review.</> }
            : {
                tone: "ok",
                body: <><strong>Ready to approve.</strong> Up to date with its pages{previewState === "loaded" ? ", checked just now" : ""}. Approving applies {changes(changeCount)} to {pages(ordered.length)}
                    {deletedItems.length ? `, ${deletedItems.length === 1 ? "one of them deleted" : `${deletedItems.length} of them deleted`}${subpages ? ` with ${subpages} subpage${subpages === 1 ? "" : "s"}` : ""}` : ""}.</>,
            };
    }

    const updateLabel = conflicts.length && left.length === 0 ? `Update with ${resolutionsText(conflicts.length)}` : "Update";
    const updateHint = !canUpdate && left.length ? `Resolve the ${left.length === 1 ? "conflict" : `${left.length} conflicts`} first` : undefined;
    const updateButton = (
        <span className="update-wrap" title={updateHint}>
            <Button text={updateLabel} type="primary" onClick={update} isDisabled={isBusy || !canUpdate} />
            {conflicts.length && !blocked ? (
                <span className="update-progress" role="img" aria-label={`${conflicts.length - left.length} of ${conflictsText(conflicts.length)} resolved`}>
                    {conflicts.length <= 12
                        ? conflicts.map((c, i) => <i key={i} className={i < conflicts.length - left.length ? "done" : undefined} />)
                        : <span className="update-progress-text">{conflicts.length - left.length}/{conflicts.length}</span>}
                </span>
            ) : null}
        </span>
    );

    return (
        <section className="change-request-review" aria-label="Change request"
            style={{ "--review-header-height": `${headerHeight}px` } as React.CSSProperties}>
            <header className="review-header" ref={headerRef}>
                <div className="review-header-row">
                    <button type="button" className="review-back" onClick={props.onClose} aria-label="Back to change requests" title="Back to change requests">←</button>
                    <h1 className="review-title" title={detail.message || "(no message)"}>{detail.message || "(no message)"}</h1>
                    <div className="review-meta">
                        <span className={`change-request-state state-${detail.state}`}>{stateLabel(detail.state)}</span>
                        <span title={detail.authorId ?? undefined}>{person(detail.authorId)}</span>
                        <span className="review-subinfo-dot" />
                        <span title={DateTimeHelper.formatDateTime(detail.createdAt)}>{DateTimeHelper.formatDateTimeAsTimeAgo(detail.createdAt)}</span>
                        {detail.updatedAt && isOpen ? (
                            <>
                                <span className="review-subinfo-dot" />
                                <span className="review-updated" title={`Revision ${revision}, ${DateTimeHelper.formatDateTime(detail.updatedAt)}`}>updated {DateTimeHelper.formatDateTimeAsTimeAgo(detail.updatedAt)}</span>
                            </>
                        ) : null}
                        {detail.client ? (<><span className="review-subinfo-dot" /><span>via {detail.client}</span></>) : null}
                        <span className="review-summary" aria-label="Summary">
                            {chips.filter(([, n]) => n > 0).map(([label, n, kind]) => (
                                <span key={kind} className="review-tag">
                                    <ReviewKindDot kind={kind} />
                                    {n} {label}
                                </span>
                            ))}
                        </span>
                    </div>

                    {isOpen && !pending ? (
                        <div className="change-request-actions">
                            {isAuthor
                                ? <Button text="Withdraw" type="secondary" onClick={() => setPending("withdraw")} isDisabled={isBusy} />
                                : <Button text="Reject" type="secondary" onClick={() => setPending("reject")} isDisabled={isBusy} />}
                            {/* the author can also just update, and leave it for a review */}
                            {isAuthor && isStale && !conflicts.length ? updateButton : null}
                            {conflicts.length ? (
                                // whoever settles the conflicts updates first, then approves
                                updateButton
                            ) : mergesCleanly ? (
                                <Button text="Update and approve" type="primary" onClick={() => { setPendingUpdate(true); setPending("approve"); }} isDisabled={isBusy} />
                            ) : (
                                <span title={isStale ? "The change request is behind its pages: Ark would refuse the approve. Update it first." : undefined}>
                                    <Button text="Approve" type="primary" onClick={() => { setPendingUpdate(false); setPending("approve"); }} isDisabled={isBusy || !!isStale} />
                                </span>
                            )}
                        </div>
                    ) : null}
                </div>

                {isStale && conflicts.length && !actionErrorText ? (
                    <RebaseConflictsSummary conflicts={conflicts} resolved={conflicts.length - left.length} isAuthor={isAuthor} authorName={authorName} attempted={attempted}
                        titleOf={titleOfPage} onSelectPage={(id) => onSelectItem(`page:${id}`)} onRejectWithConflicts={!isAuthor && !pending ? rejectWithConflicts : undefined} />
                ) : strip ? (
                    <div className={`review-strip strip-${strip.tone}`} role={strip.tone === "error" ? "alert" : "status"} aria-label={strip.label ?? "Status"}>
                        <div className="strip-body">{strip.body}</div>
                        {strip.aside ? <div className="strip-aside">{strip.aside}</div> : null}
                    </div>
                ) : null}

                {isOpen && pending ? (
                    <div className="change-request-confirm" role="group" aria-label={`Confirm ${pending}`}>
                        <div>
                            {pending === "approve"
                                ? pendingUpdate
                                    ? `Update and approve? Ark merges the edits made since into the change request (no conflicts) and applies its ${changes(changeCount)} to the space now.`
                                    : `Approve ${isAuthor ? "your own change request" : "this change request"}?${selfResolved ? ` You settled ${conflictsText(selfResolved)} yourself, so you are approving your own choices there.` : ""} Its ${ordered.length === 1 ? "change is" : `changes to ${ordered.length} pages are`} applied to the space now.`
                                : pending === "reject"
                                    ? "Reject this change request? Nothing is applied; the author sees your reason."
                                    : "Withdraw your change request? Nothing is applied."}
                        </div>
                        {pending === "reject" ? (
                            <div className="change-request-reason">
                                <label htmlFor={`reason-${detail.id}`}>Reason</label>
                                <textarea id={`reason-${detail.id}`} value={reason} rows={3} autoFocus
                                    onChange={(ev) => { setReason(ev.target.value); setReasonMissing(false); }} />
                                {reasonMissing ? <span className="change-request-error">A reason is required.</span> : null}
                            </div>
                        ) : null}
                        <div className="change-request-actions">
                            <Button text="Cancel" type="secondary" onClick={cancel} isDisabled={isBusy} />
                            <Button text={pending === "approve" && pendingUpdate ? "Confirm update and approve" : `Confirm ${pending}`} type="primary" onClick={confirm} isDisabled={isBusy} />
                        </div>
                    </div>
                ) : null}
            </header>

            {ordered.length === 0 ? (
                <div className="change-requests-empty">This change request has no changes to show.</div>
            ) : (
                <div className="review-body">
                    <aside className="review-sidebar">
                        <ReviewTree tree={review.tree} selectedKey={selected?.key} onSelect={onSelectItem} lockOf={lockOf} statusOf={statusOf} />
                        <details className="review-details">
                            <summary>Technical details</summary>
                            <dl className="change-request-facts">
                                <dt>Id</dt>
                                <dd className="change-request-id">{detail.id}</dd>
                                <dt>Created</dt>
                                <dd>{DateTimeHelper.formatDateTime(detail.createdAt)}</dd>
                                {detail.updatedAt ? (<><dt>Updated</dt><dd>{DateTimeHelper.formatDateTime(detail.updatedAt)} (revision {revision})</dd></>) : null}
                                {detail.appliedAt ? (<><dt>Applied</dt><dd>{DateTimeHelper.formatDateTime(detail.appliedAt)}</dd></>) : null}
                                <dt>Client</dt>
                                <dd>{detail.client}</dd>
                            </dl>
                            <ol className="change-request-ops">
                                {ops.map((op, idx) => (
                                    <li key={idx} className={detail.failedOpIndex === idx ? "failed-op" : undefined}>
                                        {describeOp(op, ops, titles)}
                                        {resultFor(idx) ? <span className={`op-status status-${resultFor(idx)?.status}`}> · {resultFor(idx)?.status}</span> : null}
                                    </li>
                                ))}
                            </ol>
                        </details>
                    </aside>

                    {selected ? (
                        <ReviewItemPane
                            key={selected.key}
                            item={selected}
                            spaceId={spaceId}
                            currentContent={selected.pageId !== undefined ? contents[selected.pageId] : undefined}
                            contentState={contentState}
                            isOpen={isOpen}
                            lockMark={lockOf(selected)}
                            conflicts={conflictsOn(selected)}
                            merge={isStale ? mergeOf(selected) : undefined}
                            resolutions={resolutions}
                            canResolve={isOpen}
                            onResolve={resolve}
                            labels={labelsFor(selected)}
                            previousContent={changedSince.has(selected.key) ? previousByKey?.get(selected.key)?.content ?? "" : undefined}
                            position={selectedIndex + 1}
                            total={ordered.length}
                            personLabel={person}
                            view={view}
                            onViewChange={setView}
                            onPrevious={selectedIndex > 0 ? () => goTo(selectedIndex - 1) : undefined}
                            onNext={selectedIndex < ordered.length - 1 ? () => goTo(selectedIndex + 1) : undefined}
                        />
                    ) : null}
                </div>
            )}

            {toast ? (
                <div className={`review-toast ${toast.tone}`} role="status">
                    <span>{toast.text}</span>
                    <button type="button" aria-label="Dismiss" onClick={() => setToast(undefined)}>×</button>
                </div>
            ) : null}
        </section>
    );
}
